import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Button from '../components/ui/button/Button';
import { CheckCircle, XCircle, Loader, ArrowRight } from 'lucide-react';
import PageMeta from '../components/common/PageMeta';
import bulletService from '../services/bulletService';
import tmsService from '../services/tmsService';
import appFetch from '../utils/appFetch';

const PaymentCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState<'checking' | 'success' | 'failed'>('checking');
  const [message, setMessage] = useState('');
  const [bookingId, setBookingId] = useState<number | null>(null);

  useEffect(() => {
    const paymentReference = searchParams.get('ref');
    if (paymentReference) {
      checkPaymentStatus(paymentReference);
    } else {
      setPaymentStatus('failed');
      setMessage('Invalid payment reference');
    }
  }, [searchParams]);

  const checkPaymentStatus = async (paymentReference: string) => {
    try {
      const data = await appFetch('/api/checkBookingPayment', {
        method: 'POST',
        body: JSON.stringify({ ref: paymentReference }),
      });
      
      if (data.captured === 1) {
        // Payment successful - now create TMS order first, then booking
        const pendingBookingData = sessionStorage.getItem('pendingBookingData');
        if (pendingBookingData) {
          try {
            const bookingData = JSON.parse(pendingBookingData);
            
            // First, create TMS order
            console.log('Creating TMS order...');
            
            // Prepare TMS booking data from raw booking data
            const tmsBookingData = {
              customerName: bookingData.customerName,
              customerEmail: bookingData.customerEmail,
              customerPhone: bookingData.customerPhone,
              pickupCountry: bookingData.pickupCountry,
              pickupCity: bookingData.pickupCity,
              pickupAddress: bookingData.pickupAddress,
              pickupAddressLine2: bookingData.pickupAddressLine2 || '',
              dropoffCountry: bookingData.dropoffCountry,
              dropoffCity: bookingData.dropoffCity,
              dropoffAddress: bookingData.dropoffAddress,
              dropoffAddressLine2: bookingData.dropoffAddressLine2 || '',
              packageType: bookingData.packageType,
              weight: bookingData.weight,
              weightUnit: bookingData.weightUnit,
              length: bookingData.length,
              width: bookingData.width,
              height: bookingData.height,
              packageDescription: bookingData.packageDescription,
              pickupDate: bookingData.pickupDate,
              timeSlot: bookingData.timeSlot,
              deliveryDate: bookingData.deliveryDate || bookingData.pickupDate,
              deliveryTimeSlot: bookingData.deliveryTimeSlot || bookingData.timeSlot,
              totalAmount: bookingData.totalCost || 0,
              bookingNumber: bookingData.bookingNumber || `BUL-${Date.now()}`
            };
            
            const tmsResponse = await tmsService.createTMSOrder(tmsBookingData);
            
            if (tmsResponse && tmsResponse.success && tmsResponse.data) {
              const tmsTrackingNumber = tmsResponse.data.bkrefid;
              console.log('TMS order created successfully. Tracking number:', tmsTrackingNumber);
              
              // Now create Bullet booking with TMS tracking number
              const bookingDataWithTracking = {
                ...bookingData,
                tmsTrackingNumber: tmsTrackingNumber
              };
              console.log('Booking data with TMS tracking number:', bookingDataWithTracking);
              
              const booking = await bulletService.createBooking(bookingDataWithTracking);
              
              if (booking) {
                console.log('Booking created successfully:', booking);
                
                // Clear the pending booking data
                sessionStorage.removeItem('pendingBookingData');
                
                setPaymentStatus('success');
                setMessage('Payment successful and booking created!');
                setBookingId(booking.id || null);
              } else {
                setPaymentStatus('failed');
                setMessage('TMS order created but booking creation failed. Please contact support with TMS reference: ' + tmsTrackingNumber);
              }
            } else {
              setPaymentStatus('failed');
              setMessage('Failed to create TMS order. Please contact support.');
            }
          } catch (error) {
            console.error('Error in order creation process:', error);
            setPaymentStatus('failed');
            setMessage('Payment successful but order creation failed. Please contact support.');
          }
        } else {
          setPaymentStatus('failed');
          setMessage('Payment successful but booking data not found. Please contact support.');
        }
      } else {
        setPaymentStatus('failed');
        setMessage(data.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('failed');
      setMessage('Error checking payment status');
    }
  };

  const handleContinue = () => {
    if (paymentStatus === 'success' && bookingId) {
      // Navigate to booking details or dashboard
      navigate('/dashboard');
    } else {
      // Navigate back to booking or home
      navigate('/book-delivery');
    }
  };

  return (
    <>
      <PageMeta 
        title="Payment Status - Bullet Delivery"
        description="Payment processing status for your delivery booking"
        keywords="payment status, booking confirmation, delivery payment"
      />
      
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8">
        <div className="max-w-md w-full px-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-center">Payment Status</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-6">
              {paymentStatus === 'checking' && (
                <>
                  <div className="flex justify-center">
                    <Loader className="w-16 h-16 text-blue-600 animate-spin" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold mb-2">Processing Payment</h2>
                    <p className="text-gray-600">Please wait while we verify your payment...</p>
                  </div>
                </>
              )}

              {paymentStatus === 'success' && (
                <>
                  <div className="flex justify-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-green-800 mb-2">Payment Successful!</h2>
                    <p className="text-gray-600 mb-4">{message}</p>
                    <p className="text-sm text-gray-500">
                      Your booking has been confirmed and you will receive a confirmation email shortly.
                    </p>
                  </div>
                  <Button onClick={handleContinue} className="w-full bg-green-600 hover:bg-green-700">
                    Continue to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}

              {paymentStatus === 'failed' && (
                <>
                  <div className="flex justify-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                      <XCircle className="w-8 h-8 text-red-600" />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-red-800 mb-2">Payment Failed</h2>
                    <p className="text-gray-600 mb-4">{message}</p>
                    <p className="text-sm text-gray-500">
                      Don't worry, your booking is still saved. You can try the payment again.
                    </p>
                  </div>
                  <div className="space-y-3">
                    <Button onClick={handleContinue} className="w-full">
                      Try Again
                    </Button>
                    <Button 
                      onClick={() => navigate('/dashboard')} 
                      variant="outline" 
                      className="w-full"
                    >
                      Go to Dashboard
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default PaymentCallback;
